<template>
  <el-drawer
    :model-value="modelValue"
    title="分支格子 — 录入"
    direction="btt"
    size="92%"
    class="m-branch-edit-drawer"
    append-to-body
    destroy-on-close
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <div class="m-be-root">
      <div ref="scrollEl" class="m-be-scroll">
        <p class="m-be-tip">
          点格子输入本<strong>库位</strong>分支数量；空表示该格无货。保存后写入<strong>本库存行</strong>分支明细（与其它库位互不覆盖）。
        </p>
        <p class="m-be-sum">
          当前合计 <b>{{ fmt(sumLocal) }}</b> 码
          <span class="m-be-dot">·</span>
          本行库存 <b>{{ fmt(physicalQty) }}</b> 码
          <span v-if="showMismatch" class="m-be-warn-inline">（与库存不符）</span>
        </p>

        <div v-show="activeCellLabel" class="m-be-active-preview" aria-live="polite">
          <span class="m-be-active-label">{{ activeCellLabel }}</span>
          <span class="m-be-active-value">{{ activeCellInput || '（空）' }}</span>
        </div>

        <div class="m-be-grid-wrap" role="grid" :aria-colcount="BRANCH_GRID_COLS + 1" :aria-rowcount="BRANCH_GRID_ROWS + 1">
          <span class="m-be-corner" aria-hidden="true" />
          <span v-for="c in BRANCH_GRID_COLS" :key="'h' + c" class="m-be-h">{{ colLetter(c - 1) }}</span>
          <template v-for="r in BRANCH_GRID_ROWS" :key="'row' + r">
            <span class="m-be-row-lab">{{ r }}</span>
            <div v-for="c in BRANCH_GRID_COLS" :key="r + '-' + c" class="m-be-cell-wrap">
              <input
                v-model="grid[r - 1][c - 1]"
                type="text"
                inputmode="decimal"
                autocomplete="off"
                autocorrect="off"
                spellcheck="false"
                class="m-be-input"
                :data-r="r - 1"
                :data-c="c - 1"
                placeholder=""
                @focus="(e) => onFocus(e, r - 1, c - 1)"
                @input="(e) => onInput(e, r - 1, c - 1)"
                @blur="onBlur(r - 1, c - 1)"
              />
            </div>
          </template>
        </div>
      </div>

      <div class="m-be-footer">
        <el-button size="large" @click="close">取消</el-button>
        <el-button size="large" type="warning" plain @click="confirmClearGrid">一键清空格子</el-button>
        <el-button type="primary" size="large" :loading="saving" :disabled="!canSave" @click="save">
          {{ saveLabel }}
        </el-button>
      </div>
    </div>
  </el-drawer>
</template>

<script setup>
import { ref, reactive, computed, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { updateMaterialBatch } from '@/api/materialBatches'
import { updateMaterialInventory } from '@/api/materialInventory'
import {
  BRANCH_GRID_ROWS,
  BRANCH_GRID_COLS,
  branchDetailsToGridStrings,
  gridStringsToBranchDetailsPayload,
  sumBranchDetails,
  normalizeCellInputString
} from '@/utils/materialBatchBranchGrid'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  /** 有则保存到库存行（本库位）；否则保存到料号（兼容旧逻辑） */
  inventoryId: { type: [Number, String], default: null },
  batchId: { type: [Number, String], default: null },
  initialBranchDetails: { type: Array, default: null },
  physicalQty: { type: [Number, String], default: 0 }
})

const emit = defineEmits(['update:modelValue', 'saved'])

const grid = reactive(
  Array.from({ length: BRANCH_GRID_ROWS }, () => Array(BRANCH_GRID_COLS).fill(''))
)
const saving = ref(false)
const activeRow = ref(null)
const activeCol = ref(null)
const scrollEl = ref(null)

const canSave = computed(() => !!(props.inventoryId || props.batchId))

const saveLabel = computed(() => (props.inventoryId ? '保存到本库位' : '保存到料号'))

const activeCellLabel = computed(() => {
  if (activeRow.value == null || activeCol.value == null) return ''
  return `${activeRow.value + 1}${String.fromCharCode(65 + activeCol.value)}`
})

const activeCellInput = computed(() => {
  if (activeRow.value == null || activeCol.value == null) return ''
  return grid[activeRow.value]?.[activeCol.value] ?? ''
})

function colLetter (c) {
  return String.fromCharCode(65 + c)
}

function reinitGrid () {
  const src = branchDetailsToGridStrings(props.initialBranchDetails)
  for (let r = 0; r < BRANCH_GRID_ROWS; r++) {
    for (let c = 0; c < BRANCH_GRID_COLS; c++) {
      grid[r][c] = src[r][c] ?? ''
    }
  }
}

function clearGridAll () {
  for (let r = 0; r < BRANCH_GRID_ROWS; r++) {
    for (let c = 0; c < BRANCH_GRID_COLS; c++) {
      grid[r][c] = ''
    }
  }
  activeRow.value = null
  activeCol.value = null
}

async function confirmClearGrid () {
  try {
    await ElMessageBox.confirm(
      '将清空当前界面上所有格子的数量（未点保存不会写入系统）。是否继续？',
      '一键清空分支格子',
      { type: 'warning', confirmButtonText: '清空', cancelButtonText: '取消' }
    )
    clearGridAll()
  } catch {
    /* 取消 */
  }
}

const sumLocal = computed(() => {
  const payload = gridStringsToBranchDetailsPayload(grid)
  return sumBranchDetails(payload == null ? [] : payload)
})

const physical = computed(() => Number(props.physicalQty) || 0)
const showMismatch = computed(() => Math.abs(sumLocal.value - physical.value) > 0.01)

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      reinitGrid()
    }
  }
)

watch(
  () => props.initialBranchDetails,
  () => {
    if (props.modelValue) reinitGrid()
  },
  { deep: true }
)

function onFocus (e, r, c) {
  activeRow.value = r
  activeCol.value = c
  const t = e.target
  if (t && typeof t.select === 'function') {
    t.select()
  }
  // 只在中间滚动区内滚入视野，不带动底部保存条
  requestAnimationFrame(() => {
    try {
      const sc = scrollEl.value
      if (!sc || !t) return
      const cell = t.getBoundingClientRect()
      const box = sc.getBoundingClientRect()
      const pad = 12
      if (cell.top < box.top + pad) {
        sc.scrollTop -= box.top + pad - cell.top
      } else if (cell.bottom > box.bottom - pad) {
        sc.scrollTop += cell.bottom - (box.bottom - pad)
      }
    } catch (_) {
      /* ignore */
    }
  })
}

function onInput (_e, r, c) {
  activeRow.value = r
  activeCol.value = c
}

function onBlur (r, c) {
  grid[r][c] = normalizeCellInputString(grid[r][c])
  activeRow.value = null
  activeCol.value = null
}

function fmt (v) {
  if (v == null || v === '') return '0'
  const n = Number(v)
  if (!Number.isFinite(n)) return String(v)
  const r = Math.round(n * 10) / 10
  return Number.isInteger(r) ? String(r) : r.toFixed(1).replace(/\.0$/, '')
}

function close () {
  emit('update:modelValue', false)
}

async function save () {
  if (!props.inventoryId && !props.batchId) {
    ElMessage.warning('缺少库存行或料号')
    return
  }
  for (let r = 0; r < BRANCH_GRID_ROWS; r++) {
    for (let c = 0; c < BRANCH_GRID_COLS; c++) {
      grid[r][c] = normalizeCellInputString(grid[r][c])
    }
  }
  const branch_details = gridStringsToBranchDetailsPayload(grid)
  saving.value = true
  try {
    let res
    if (props.inventoryId) {
      res = await updateMaterialInventory(props.inventoryId, { branch_details })
    } else {
      res = await updateMaterialBatch(props.batchId, { branch_details })
    }
    if (res.success) {
      ElMessage.success(props.inventoryId ? '本库位分支明细已保存' : '分支明细已保存')
      emit('saved', branch_details)
      close()
    } else {
      ElMessage.error(res.message || '保存失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '保存失败')
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.m-be-tip {
  margin: 0 0 8px;
  font-size: 13px;
  color: #64748b;
  line-height: 1.45;
}

.m-be-sum {
  margin: 0 0 12px;
  font-size: 14px;
  color: #0f172a;
}

.m-be-dot {
  margin: 0 6px;
  color: #94a3b8;
}

.m-be-warn-inline {
  color: #ef4444;
  font-weight: 700;
}

.m-be-active-preview {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  background: linear-gradient(135deg, #eff6ff 0%, #f8fafc 100%);
  border: 1px solid #bfdbfe;
  min-height: 52px;
}

.m-be-active-label {
  font-size: 14px;
  font-weight: 700;
  color: #334155;
  flex-shrink: 0;
}

.m-be-active-value {
  flex: 1;
  text-align: right;
  font-size: clamp(22px, 6.5vw, 30px);
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
  color: #0f172a;
  word-break: break-all;
  line-height: 1.15;
}

/* 11 列：行号列 + 10 数据列；minmax(0) 避免小屏撑破视口 */
.m-be-grid-wrap {
  --m-be-label-col: 2rem;
  display: grid;
  grid-template-columns: var(--m-be-label-col) repeat(10, minmax(0, 1fr));
  gap: 6px;
  align-items: stretch;
  padding-bottom: 4px;
  max-width: 100%;
  width: 100%;
  box-sizing: border-box;
}

.m-be-corner {
  min-width: 0;
}

.m-be-h {
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  line-height: 1.2;
}

.m-be-row-lab {
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  line-height: 1.2;
}

.m-be-cell-wrap {
  min-width: 0;
}

.m-be-input {
  width: 100%;
  min-height: 48px;
  height: 100%;
  box-sizing: border-box;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  color: #0f172a;
  background: #fff;
  padding: 6px 4px;
  line-height: 1.2;
  /* 减少 iOS 在 input 上抢手势导致外层滚不动 */
  touch-action: manipulation;
}

.m-be-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}

.m-be-footer .el-button {
  flex: 1 1 auto;
  min-width: 7.5rem;
}
</style>

<!-- 非 scoped：抽屉 append-to-body 后仍能命中布局，并保证 iOS 高度链 -->
<style>
.m-branch-edit-drawer.el-drawer {
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden !important;
  width: 100% !important;
  max-width: 100vw !important;
  box-sizing: border-box;
}

.m-branch-edit-drawer .el-drawer__header {
  flex: 0 0 auto;
  margin-bottom: 0;
  padding-bottom: 12px;
}

.m-branch-edit-drawer .el-drawer__body {
  flex: 1 1 0% !important;
  min-height: 0 !important;
  height: auto !important;
  max-height: none !important;
  padding: 0 !important;
  overflow: hidden !important;
  display: flex !important;
  flex-direction: column !important;
  box-sizing: border-box;
}

.m-branch-edit-drawer .m-be-root {
  flex: 1 1 0%;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
}

.m-branch-edit-drawer .m-be-scroll {
  flex: 1 1 0%;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  touch-action: pan-y;
  padding: 10px 12px 8px;
}

.m-branch-edit-drawer .m-be-footer {
  flex: 0 0 auto;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-end;
  padding: 12px 12px calc(12px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid #e2e8f0;
  background: #fff;
  box-shadow: 0 -6px 16px rgba(15, 23, 42, 0.06);
  z-index: 5;
}
</style>
